<template>
  <div class="analytics-report">
    <scroll-content class="scroll_content" ref="scroll_content">
      <ui-loading-circle v-if="isLoading" :applyBackground="true"/>
      <template v-else>
        <div class="info clearfix">
          <h2>{{ $t('analytics.title') }}</h2>
          <span class="bar"></span>
          <span class="period">
            {{ reportPeriod.start | dotDate }} ~
            {{ reportPeriod.end | dotDate }}
          </span>
          <template v-if="rawData">
            <span class="total">Total:</span>
            <span class="pv">{{ rawData.pvTotal | thousand }} PV,</span>
            <span class="uv">{{ rawData.uvTotal | thousand }} UV</span>
          </template>
        </div>
        <ul class="notice">
          <li v-if="clippedAt">{{ $t('heatmapV2.notice.clipped', { time: clippedTime }) }}</li>
          <li>{{ $t('analytics.notice.list1') }}</li>
          <li>{{ $t('analytics.notice.list2') }}</li>
        </ul>

        <div v-if="rawData && rawData.pvTotal">
          <div class="content-section clearfix">
            <div class="content-box-outer">
              <div class="content-box">
                <h3 v-html="$t('analytics.content.pv.title')"></h3>
                <simple-analytics
                  type="pv"
                  name="PV"
                  :dailyData="dailyData"
                  color="#ec0047"/>
              </div>
            </div>
            <div class="content-box-outer">
              <div class="content-box">
                <h3 v-html="$t('analytics.content.uv.title')"></h3>
                <uv-analytics
                  :dailyData="dailyData"
                  :isAMP="applyData.bAMP"
                  :report-period="reportPeriod"
                />
              </div>
            </div>
          </div>

          <div class="content-section clearfix">
            <div class="content-box-outer">
              <div class="content-box">
                <h3 v-html="$t('analytics.content.pvPerUv.title')"></h3>
                <simple-analytics
                  type="avgPvPerUv"
                  name="PV / UV"
                  :dailyData="dailyData"
                  color="#ab85ff"/>
              </div>
            </div>
            <div class="content-box-outer">
              <div class="content-box">
                <h3>{{ $t('analytics.content.avgDuration.title') }}</h3>
                <simple-analytics
                  type="avgDuration"
                  :name="$t('common.reportIndicator.meanResidenceTime.label')"
                  :dailyData="dailyData"
                  color="#43b6c9"/>
              </div>
            </div>
          </div>

          <div class="content-section clearfix" v-if="!applyData.bAMP">
            <div class="content-box-outer" >
              <div class="content-box">
                <h3>{{ $t('analytics.content.exitRate.title') }}</h3>
                <simple-analytics
                  type="exitRate"
                  :name="$t('analytics.content.exitRate.title') + '(%)'"
                  :dailyData="dailyData"
                  color="#43b6c9"/>
              </div>
            </div>
            <div class="content-box-outer">
              <div class="content-box">
                <h3>{{ $t('analytics.content.inflow.title') }}</h3>
                <inflow-channel-analytics
                  :applyData="applyData"
                  :inflowData="inflowData"/>
              </div>
            </div>
          </div>

          <div class="section-title" v-if="!applyData.bAMP">
            <h2>{{ $t('analytics.behaviorPattern.title') }}</h2>
          </div>

          <div class="content-section clearfix" v-if="!applyData.bAMP">
            <div class="content-box" style="height: 565px;">
              <conversion-analytics
                :applyData="applyData"
                :conversionData="conversionData"/>
            </div>
          </div>

          <div class="section-title" v-if="applyData.bAMP">
            <h2>{{ $t('analytics.ampRefer.title') }}</h2>
          </div>

          <div class="content-section amp clearfix" v-if="applyData.bAMP">
            <div class="content-box" style="height: 565px;">
              <inflow-channel-analytics
                :applyData="applyData"
                :inflowData="inflowData"/>
              <conversion-analytics
                :applyData="applyData"
                :conversionData="conversionData"/>
            </div>
          </div>

          <div class="section-title with-notice">
            <h2>{{ $t('analytics.environmentAnalytics.title') }}</h2>
            <ul class="notice">
              <li>{{ $t('analytics.environmentAnalytics.desc') }}</li>
            </ul>
          </div>

          <div class="content-section clearfix">
            <div class="content-box-outer">
              <div class="content-box content-box-larger">
                <h3>{{ $t('analytics.content.displayResolution.title') }}</h3>
                <div class="control">
                  <ui-selectbox class="chart_options" v-model="resolutionDevice"
                    :items="deviceOptions"/>
                </div>
                <resolution-analytics
                  mode="chart"
                  :resolutionData="resolutionData"
                  :device="resolutionDevice"/>
              </div>
            </div>
            <div class="content-box-outer">
              <div class="content-box content-box-larger">
                <h3>{{ $t('analytics.content.displayResolutionTable.title') }}</h3>
                <div class="control">
                  <ui-selectbox class="chart_options" v-model="resolutionDetailDevice"
                    :items="deviceOptions"/>
                </div>
                <resolution-analytics
                  mode="list"
                  :resolutionData="resolutionData"
                  :device="resolutionDetailDevice"/>
              </div>
            </div>
          </div>

          <div class="content-section clearfix">
            <div class="content-box-outer">
              <div class="content-box content-box-larger content-box-combined">
                <h3>{{ $t('analytics.content.deviceAnalysis.title') }}</h3>
                <p class="highlight">{{ $t('analytics.content.deviceAnalysis.highlight') }}</p>
                <mixed-chart-analytics
                  type="device"
                  :name="$t('common.device')"
                  :rawData="rawData"
                  :dailyData="rawData.daily"
                  :topSize="4"
                  :addEtc="false"
                  :pathData="devicePathData"/>
              </div>
            </div>
            <div class="content-box-outer">
              <div class="content-box content-box-larger">
                <h3>{{ $t('analytics.content.visitPerCountry.title') }}</h3>
                <country-analytics
                  :rawData="rawData"
                  :countryData="countryData"/>
              </div>
            </div>
          </div>

          <div class="content-section clearfix">
            <div class="content-box-outer">
              <div class="content-box content-box-larger content-box-combined">
                <h3>{{ $t('analytics.content.operatingSystem.title') }}</h3>
                <div class="control">
                  <ui-checkbox v-model="osPerVersions">
                    {{ $t('analytics.content.operatingSystem.checkboxLabel') }}
                  </ui-checkbox>
                  <span class="delimiter"></span>
                  <ui-selectbox class="chart_options" v-model="osDevice"
                    :items="osDeviceOptions"/>
                </div>

                <mixed-chart-analytics
                  :name="$t('common.os')"
                  :dailyData="rawData.daily"
                  :topSize="4"
                  :addEtc="true"
                  :pathData="osPathData"
                  :perVersions="osPerVersions"/>
              </div>
            </div>
            <div class="content-box-outer">
              <div class="content-box content-box-larger">
                <h3>{{ $t('analytics.content.browserAnalysis.title') }}</h3>
                <div class="control">
                  <ui-checkbox v-model="browserPerVersions">
                    {{ $t('analytics.content.browserAnalysis.checkboxLabel') }}
                  </ui-checkbox>
                  <span class="delimiter"></span>
                  <ui-selectbox class="chart_options" v-model="browserDevice" :width="146"
                    :items="browserDeviceOptions"/>
                </div>

                <mixed-chart-analytics
                  :name="$t('common.browser')"
                  :dailyData="rawData.daily"
                  :topSize="4"
                  :addEtc="true"
                  :pathData="browserPathData"
                  :perVersions="browserPerVersions"/>
              </div>
            </div>
          </div>
          <back-to-top refName="scroll_content"/>
        </div>
        <div v-else class="no-pv">{{ $t('analytics.noPvData') }}</div>
      </template>
    </scroll-content>
  </div>
</template>

<script>
import { getPercentage, utcToLocalTimezone } from '@/libs/utils'
import {
  osDeviceOptions,
  browserDeviceOptions,
  deviceOptions
} from '@/libs/analytics-helper'
import { mapState } from 'vuex'
import { getAnalyticsData } from '@/api/report'
import { formatNoticeTime } from '@/libs/heatmap-v2/notice'
import UiCheckbox from '@/components/ui/UiCheckbox'
import UiSelectbox from '@/components/ui/UiSelectbox'
import SimpleAnalytics from '@/components/analytics/SimpleAnalytics'
import UvAnalytics from '@/components/analytics/UvAnalytics'
import InflowChannelAnalytics from '@/components/analytics/InflowChannelAnalytics'
import MixedChartAnalytics from '@/components/analytics/MixedChartAnalytics'
import ConversionAnalytics from '@/components/analytics/ConversionAnalytics'
import ResolutionAnalytics from '@/components/analytics/ResolutionAnalytics'
import CountryAnalytics from '@/components/analytics/CountryAnalytics'
import ScrollContent from '@/components/ui/ScrollContent'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle'
import BackToTop from '@/components/ui/BackToTop'

export default {
  name: 'AnalyticsReport',
  components: {
    UiCheckbox,
    UiSelectbox,
    SimpleAnalytics,
    UvAnalytics,
    InflowChannelAnalytics,
    MixedChartAnalytics,
    ConversionAnalytics,
    ResolutionAnalytics,
    CountryAnalytics,
    ScrollContent,
    UiLoadingCircle,
    BackToTop
  },
  data () {
    return {
      isLoading: false,
      rawData: null,
      clippedAt: null,
      resolutionDevice: null,
      resolutionDetailDevice: null,
      osDevice: null,
      osPerVersions: false,
      browserDevice: null,
      browserPerVersions: false
    }
  },
  computed: {
    clippedTime () {
      return this.clippedAt ? formatNoticeTime(this.clippedAt, this.applyData.timezone) : ''
    },
    ...mapState([
      'reportPeriod',
      'applyData'
    ]),
    aid () {
      return this.$route.params.aid
    },
    apiParams () {
      return {
        aid: this.aid,
        startDate: this.reportPeriod.start,
        endDate: this.reportPeriod.end
      }
    },
    osPathData () {
      return this.osDeviceOptions.find(item => item.value === this.osDevice)
    },
    browserPathData () {
      return this.browserDeviceOptions.find(item => item.value === this.browserDevice)
    },
    dailyData () {
      const data = this.rawData.daily.map(item => {
        let avgPvPerUv = item.uv ? item.pv / item.uv : 0
        avgPvPerUv = Number(avgPvPerUv.toFixed(1))
        const exitRate = getPercentage(item.pv - item.cvr, item.pv, 1)

        return {
          timestamp: utcToLocalTimezone(item.day, this.applyData.timezoneName).getTime(),
          pv: item.pv,
          uv: item.uv,
          newUv: item.newUv,
          returnUv: item.uv - item.newUv,
          avgPvPerUv: avgPvPerUv,
          avgDuration: item.sessTime || 0,
          exitRate: exitRate
        }
      })
      // console.log(data)
      return data
    },
    inflowData () {
      return {
        items: this.rawData.refer,
        referEtcCount: this.rawData.referEtc
      }
    },
    conversionData () {
      return {
        items: this.rawData.cvr,
        conversionCount: this.rawData.cvrExist,
        totalCount: this.rawData.pvTotal,
        refererItems: this.rawData.refer
      }
    },
    resolutionData () {
      const items = []
      let maxUv = 0
      let totalUv = 0

      for (const item of this.rawData.res) {
        const device = item.device
        const [width, height] = item.res.split('_').map(item => Number(item))

        if (width > 10000 || height > 10000) {
          continue
        }

        maxUv = Math.max(maxUv, item.count)
        items.push({
          device,
          width,
          height,
          uv: item.count
        })
        totalUv += item.count
      }

      items.sort((a, b) => {
        return b.uv - a.uv
      })

      return {
        items,
        maxUv,
        totalUv
      }
    },
    countryData () {
      return this.rawData.geo.filter(item => item.countryFullname).map(item => {
        return {
          name: item.countryFullname,
          code: item.country,
          value: item.count
        }
      })
    }
  },
  watch: {
    apiParams (value) {
      this.loadData().catch(console.error)
    }
  },
  created () {
    this.osDeviceOptions = osDeviceOptions
    this.osDevice = this.osDeviceOptions[0].value
    this.browserDeviceOptions = browserDeviceOptions
    this.browserDevice = this.browserDeviceOptions[0].value
    this.devicePathData = {
      name: 'default',
      paths: ['device', '{device}'],
      countPaths: ['device']
    }
    this.deviceOptions = deviceOptions
    this.resolutionDevice = this.deviceOptions[0].value
    this.resolutionDetailDevice = this.deviceOptions[0].value
    this.loadData().catch(console.error)
  },
  methods: {
    loadData () {
      this.isLoading = true
      return getAnalyticsData(this.apiParams, this.applyData.timezone, outcome => {
        this.clippedAt = outcome.clippedAt || null
      }).then(data => {
        this.rawData = data.analytics_data
        this.isLoading = false
      }).catch(err => {
        this.isLoading = false
        throw err
      })
    }
  }
}
</script>

<style lang="scss">
.analytics-report {
  h2 {
    font-size: 22px;
    font-weight: 500;
    color: #444;
  }

  .section-title {
    h2 {
      padding-top: 36px;
      padding-bottom: 26px;
    }
    &.with-notice {
      h2 {
        padding-bottom: 16px;
      }
      .notice {
        margin-bottom: 30px;
      }
    }
  }

  .info {
    margin-bottom: 16px;

    h2,
    &> span {
      display: inline-block;
      vertical-align: middle;
    }
    .bar {
      width: 1px;
      height: 15px;
      background-color: #d7d7d7;
      margin: 3px 10px 0 10px;
    }
    .period,
    .total,
    .pv,
    .uv {
      font-size: 15px;
      font-weight: 500;
    }
    .period {
      color: #444;
      margin-right: 20px;
      font-weight: 500;
    }
    .total {
      color: #767676;
      margin-right: 8px;
    }
    .pv {
      color: $brandColor;
      margin-right: 4px;
    }
    .uv {
      color: #43b6c9
    }
  }
  .notice {
    margin-bottom: 24px;
    font-size: 12px;
    font-weight: 500;
    color: #767676;

    li {
      padding-left: 5px;
      margin-bottom: 6px;
    }

    li:before {
      content: '';
      display: inline-block;
      vertical-align: top;
      margin-top: 3px;
      margin-right: 4px;
      width: 1px;
      height: 1px;
      border: solid 1px #979797;
      background-color: #d8d8d8;
    }
  }

  .content-section {
    margin-bottom: 30px;

    .content-box-outer {
      float: left;
      width: 50%;
      box-sizing: border-box;

      &:nth-child(odd) {
        padding-right: 15px;
      }

      &:nth-child(even) {
        padding-left: 15px;
      }
    }

    .content-box {
      position: relative;
      box-sizing: border-box;
      height: 270px;
      border-radius: 14px;
      background-color: #ffffff;
      padding: 25px;
      box-sizing: border-box;
      box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);

      &.content-box-larger {
        height: 430px;
      }

      h3 {
        padding-bottom: 24px;
        font-size: 15px;
        font-weight: 500;
        color: #2f2f2f;

        span {
          font-size: 13px;
          color: #767676;
        }
      }

      .control {
        position: absolute;
        right: 15px;
        top: 15px;

        .delimiter {
          display: inline-block;
          vertical-align: middle;
          width: 1px;
          height: 14px;
          background-color: #d7d7d7;
          margin: 0 16px 0 14px;
        }
      }

      .highlight {
        position: absolute;
        right: 25px;
        top: 25px;
        font-size: 12px;
        text-align: right;
        color: $brandColor;
        font-weight: 500;
      }
    }

    .content-box-combined {
      .pie_chart {
        float: left;
      }
      .top-list {
        float: right;
      }

    }
  }
  .chart_options {
    vertical-align: middle;
    width: 126px;
  }
  .no-pv {
    @include positionXYcenter;
    min-width: auto;
    text-align: center;
    font-size: 13px;
    color: #444;
    line-height: 2.31;
    border-radius: 14px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #ffffff;
    padding: 16px 50px 14px 50px;
  }
}
</style>
